import { generateTitle } from 'utils/charts/common';
import {
    GREEN_CIRCLE_URL,
    ORANGE_CIRCLE_URL,
    PINK_RED_CIRCLE_URL
} from 'utils/charts/constants';
import { OUTLIER_DETECTION_TITLE } from 'utils/charts/constants';

/**
 * Handle tooltip data for the scatter plot chart
 * @param {Object[]} outliers List of all the rms with their info to plot the scatter and tooltip
 * @param {String} outliers[].rmRegion the market the rm belongs to
 * @param {String} outliers[].rmGroup the team the rm belongs to
 * @param {String} outliers[].rmEmployeeId the rm's id
 * @param {Number} outliers[].totalPortfolio the total number of portfolio risk alerts
 * @param {Number} outliers[].percentPortfolio the percentage of portfolio risk (to plot on X Axis)
 * @param {Number} outliers[].totalConcentration the total number of concentration risk alerts
 * @param {Number} outliers[].percentConcentration the percentage of concentration risk (to plot on Y Axis)
 * @param {Number} outliers[].rank the rank of the RM
 * @param {Number} outliers[].score the score of the RM
 * @param {Number} concentrationRiskThreshold
 * @param {Number} portfolioRiskThreshold
 */
const tooltip = (
    outliers,
    concentrationRiskThreshold,
    portfolioRiskThreshold
) => {
    return {
        formatter: function (params) {
            const { data } = params;
            const portfolioRiskValue = data[0];
            const concentrationRiskValue = data[1];
            const selectedOutlier = outliers.find(
                (o) =>
                    o.percentConcentration === concentrationRiskValue &&
                    o.percentPortfolio === portfolioRiskValue
            );
            return (
                '<span style="line-height: 30px;">CTO Code</span>' +
                '<span style="font-weight: 400; margin-left: 8px; margin-bottom: 5px; color: #929292;">' +
                selectedOutlier.rmEmployeeId +
                '</span>' +
                '<br><span style="line-height: 30px;">Team</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                selectedOutlier.rmGroup +
                '</span>' +
                '<br><span style="line-height: 30px;">Market</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                selectedOutlier.rmRegion +
                '</span>' +
                '<br><span style="line-height: 30px;">S2 Rank</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                selectedOutlier.rank +
                '</span>' +
                '<br><span style="line-height: 30px;">S2 Risk Score</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                selectedOutlier.score +
                '</span>' +
                '<br><span style="line-height: 30px;">% of Concentration Risk Alert</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                selectedOutlier.percentConcentration +
                '%' +
                '</span>' +
                '<br><span style="line-height: 30px;"># of Concentration Risk Alert</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                selectedOutlier.totalConcentration +
                '</span>' +
                '<br><span style="line-height: 30px;">Concentration Risk Threshold</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                concentrationRiskThreshold +
                '%' +
                '</span>' +
                '<br><span style="line-height: 30px;">% of Portfolio Risk Mismatch Alert</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                selectedOutlier.percentPortfolio +
                '%' +
                '</span>' +
                '<br><span style="line-height: 30px;"># of Portfolio Risk Mismatch Alert</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                selectedOutlier.totalPortfolio +
                '</span>' +
                '<br><span style="line-height: 30px;">Portfolio Risk Mismatch Threshold</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                portfolioRiskThreshold +
                '%' +
                '</span>'
            );
        },
        textStyle: {
            color: '#000000',
            fontWeight: 700,
            fontSize: '10px'
        },
        position: 'right',
        extraCssText:
            'box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.08); ' +
            'border: 1px solid white; ' +
            'border-radius: 16px; ' +
            'padding: 16px;'
    };
};

const moreThanFiveAboveThresholdGraphic = {
    image: {
        type: 'image',
        style: {
            image: GREEN_CIRCLE_URL,
            width: 12,
            height: 12,
            opacity: 0.4
        },
        cursor: 'default',
        bottom: 10,
        left: '13%'
    },
    text: {
        type: 'text',
        style: {
            text: '>5% below Threshold',
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '16%'
    }
};

const lessThanEqualToFivePercentBelowThresholdGraphic = {
    image: {
        type: 'image',
        style: {
            image: ORANGE_CIRCLE_URL,
            width: 12,
            height: 12
        },
        cursor: 'default',
        bottom: 10,
        left: '37%'
    },
    text: {
        type: 'text',
        style: {
            text: '\u22645% below Threshold',
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '40%'
    }
};

const aboveThresholdGraphic = {
    image: {
        type: 'image',
        style: {
            image: PINK_RED_CIRCLE_URL,
            width: 12,
            height: 12
        },
        cursor: 'default',
        bottom: 10,
        left: '62%'
    },
    text: {
        type: 'text',
        style: {
            text: 'Above Threshold',
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '65%'
    }
};

/**
 * Generate options for outlier detection scatter plot chart
 * @param {Object} data Outliers data
 * @param {Number} data.concentrationThreshold The concentration risk threshold for all rms
 * @param {Number} data.portfolioThreshold The portfolio risk threshold for all rms
 * @param {Object[]} data.outliers List of all the rms with their info to plot the scatter and tooltip
 * @param {String} data.outliers[].rmRegion the market the rm belongs to
 * @param {String} data.outliers[].rmGroup the team the rm belongs to
 * @param {String} data.outliers[].rmEmployeeId the rm's id
 * @param {Number} data.outliers[].totalPortfolio the total number of portfolio risk alerts
 * @param {Number} data.outliers[].percentPortfolio the percentage of portfolio risk (to plot on X Axis)
 * @param {Number} data.outliers[].totalConcentration the total number of concentration risk alerts
 * @param {Number} data.outliers[].percentConcentration the percentage of concentration risk (to plot on Y Axis)
 * @param {Number} data.outliers[].rank the rank of the RM
 * @param {Number} data.outliers[].score the score of the RM
 */
export const scenarioTwoScatterPlotOptions = (data) => {
    const { outliers, concentrationThreshold, portfolioThreshold } = {
        ...data
    };
    const cnThresholdMinusTen = concentrationThreshold - 5;
    const pfThresholdMinusTen = portfolioThreshold - 5;
    const greenRms =
        outliers &&
        outliers.filter((o) => {
            // more or equal to threshold - 10 for both concentration and portfolio
            // <= threshold - 10
            return (
                o.percentConcentration <= cnThresholdMinusTen &&
                o.percentPortfolio <= pfThresholdMinusTen
            );
        });

    const yellowRms =
        outliers &&
        outliers.filter((o) => {
            // less than threshold - 10 for either concentration or portfolio and
            // lower than threshold for both concentration and portfolio
            // Range: > threshold - 10 and less than threshold
            return (
                (o.percentConcentration > cnThresholdMinusTen ||
                    o.percentPortfolio > pfThresholdMinusTen) &&
                o.percentConcentration < concentrationThreshold &&
                o.percentPortfolio < portfolioThreshold
            );
        });

    const redRms =
        outliers &&
        outliers.filter(
            (o) =>
                // either concentration OR portfolio is above threshold
                o.percentConcentration > concentrationThreshold ||
                o.percentPortfolio > portfolioThreshold
        );

    const highestPercentPortfolio =
        outliers &&
        Math.max(
            ...outliers.reduce(
                (acc, curr) => [...acc, curr.percentPortfolio],
                []
            )
        );
    const highestPercentConcentration =
        outliers &&
        Math.max(
            ...outliers.reduce(
                (acc, curr) => [...acc, curr.percentConcentration],
                []
            )
        );
    const highestPercentForPfAndConc =
        highestPercentPortfolio || highestPercentConcentration
            ? Math.max(highestPercentPortfolio, highestPercentConcentration)
            : 0;
    const axisMax = Math.round((highestPercentForPfAndConc + 5) / 10) * 10;

    return {
        title: generateTitle(OUTLIER_DETECTION_TITLE),
        grid: {
            left: '58px',
            width: '400px',
            height: '290px',
            containLabel: true
        },
        tooltip: tooltip(outliers, concentrationThreshold, portfolioThreshold),
        xAxis: {
            type: 'value',
            scale: true,
            name: '% of Portfolio Risk Mismatch Alerts',
            nameLocation: 'center',
            nameGap: 40,
            nameTextStyle: {
                color: '#7FADBF',
                fontWeight: 600,
                fontSize: 12,
                width: '100%',
                verticalAlign: 'middle'
            },
            axisLabel: {
                formatter: '{value}%'
            },
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            },
            splitLine: {
                show: false
            },
            min: 0,
            max: axisMax || null,
            interval: 5
        },
        yAxis: {
            type: 'value',
            scale: true,
            name: '% of Concentration Risk Alerts',
            nameLocation: 'center',
            nameGap: 75,
            nameTextStyle: {
                color: '#7FADBF',
                fontWeight: 600,
                fontSize: 12,
                width: '100%',
                verticalAlign: 'middle'
            },
            axisLabel: {
                formatter: '{value}%',
                showMinLabel: false
            },
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            },
            splitLine: {
                show: false
            },
            min: 0,
            max: axisMax || null,
            interval: 5
        },
        dataZoom: [
            {
                type: 'inside',
                startValue: 0,
                endValue: axisMax
            }
        ],
        series: [
            {
                type: 'scatter',
                // prettier-ignore
                data: greenRms ? greenRms.map(rm => [rm.percentPortfolio, rm.percentConcentration]) : [],
                itemStyle: {
                    color: '#31BC8A'
                },
                emphasis: {
                    focus: 'self'
                }
            },
            {
                type: 'scatter',
                // prettier-ignore
                data: yellowRms ? yellowRms.map(rm => [rm.percentPortfolio, rm.percentConcentration]) : [],
                itemStyle: {
                    color: '#F8A627'
                },
                emphasis: {
                    focus: 'self'
                }
            },
            {
                type: 'scatter',
                // prettier-ignore
                data: redRms ? redRms.map(rm => [rm.percentPortfolio, rm.percentConcentration]) : [],
                itemStyle: {
                    color: '#EB4560'
                },
                emphasis: {
                    focus: 'self'
                }
            }
        ],
        graphic: [
            moreThanFiveAboveThresholdGraphic.image,
            moreThanFiveAboveThresholdGraphic.text,
            lessThanEqualToFivePercentBelowThresholdGraphic.image,
            lessThanEqualToFivePercentBelowThresholdGraphic.text,
            aboveThresholdGraphic.image,
            aboveThresholdGraphic.text
        ]
    };
};
